import type { ArcgisDashboardCrawl } from '../../../services/api/domains/arcgisDashboards.api'
import type { RobotChatContext } from '../../../services/api/domains/chatbot.api'
import type { FloorLevel } from '../Map/engines/ArcGISEngine/floors/floor-model'
import type { CameraState } from '../Map/types'

export type { RobotChatContext }

export type MapFeature = {
  id?: string | number
  geometry: GeoJSON.Geometry
  properties?: Record<string, unknown>
}

export type MapStateSnapshot = {
  center: [number, number]
  zoom: number
  visibleLayerIds: string[]
  engineType: 'openlayers' | 'arcgis'
  /** '2d' for all OL maps and ArcGIS maps not in 3D mode. */
  viewType: '2d' | '3d'
  /** Populated only when `viewType === '3d'`. `fov` is reserved for v2 chatbot tools. */
  camera?: Omit<CameraState, 'fov'>
  /** Slides available on this saved map. Populated when at least one slide is defined. */
  slides?: Array<{ id: string; title: string }>
  /**
   * Layer groups — present when the map has at least one group node (Task 7 Delta 1).
   * Each entry names a group and the layerIds that are its direct leaf children.
   */
  groups?: Array<{ id: string; title: string; childLayerIds: string[] }>
  /**
   * 3D scene environment — present when viewType === '3d' and the map has
   * environment settings (Task 7 Delta 4).
   */
  environment?: {
    timeOfDay?: string
    weather?: string
    atmosphere?: 'on' | 'off'
    waterReflections?: boolean
    sunPosition?: { azimuth_deg: number; elevation_deg: number }
  }
}

export type LayerSampleSnapshot = {
  layerId: string
  layerName: string
  layerType: 'postgresql' | 'wfs' | 'arcgis_feature' | 'geojson' | 'csv_table' | 'wms'
  fields?: Array<{ name: string; type: string }>
  sample?: Record<string, unknown>
  /**
   * Source URL of the layer — used by the AI service to distinguish
   * ArcGIS Online (services.arcgis.com) from ArcGIS Enterprise (Task 7 Delta 2).
   */
  sourceUrl?: string
}

/**
 * An on-map CCTV camera: a `cctv_id` point present on the dashboard map, resolved
 * to a registered `CctvCamera`. Surfaced to the chatbot (via the `cctv-cameras`
 * request) so it can switch the followed camera by name / location / relative ref.
 */
export type CctvCameraOnMap = {
  cameraId: string
  name: string
  kind: 'hik' | 'rtsp' | 'onvif'
  cctvId: string
  lng?: number
  lat?: number
  layerId: string
  layerName: string
  ptzCapable?: boolean
}

/** One floor-aware building surfaced to the chatbot (mirrors the AI-service FloorContext). */
export type FloorContext = {
  buildingId: string
  buildingName: string
  levels: FloorLevel[]
  floorField: string
  layerId: string
  activeFloor: number | null
}

export type BridgeEvents = {
  'highlight-features': { layerId: string; features: MapFeature[]; highlightColor?: string }
  'clear-highlights': Record<string, never>
  'zoom-to-location': { center: [number, number]; zoom: number }
  'zoom-to-feature': { geometry: GeoJSON.Geometry; zoomPadding?: number }
  'toggle-layer': { layerId: string; visible: boolean }
  /** Draws a circle overlay. `label` is an optional human-readable caption rendered at the center (e.g. `"5km from KLCC"`). */
  'show-radius-circle': { center: [number, number]; radiusMeters: number; label?: string }
  'show-measurement': {
    start: [number, number]
    end: [number, number]
    distance: number
    unit: 'meters' | 'kilometers' | 'miles' | 'feet'
  }
  'clear-map-overlays': Record<string, never>
  'refresh-element': { elementId: string }
  /**
   * The dashboard's own ELEMENT LIST changed on the server — a new element was
   * created, or an existing one's config was rewritten — so the page must
   * re-read the dashboard rather than re-fetch one element's data.
   * `refresh-element` cannot do this: it targets an element the page is already
   * rendering. Emitted by the assistant's `add_dashboard_element` /
   * `configure_dashboard_element` tools, without which a successfully added
   * element stays invisible until the user reloads.
   */
  'dashboard:elements-changed': { elementId?: string; action: 'added' | 'configured' }
  'show-analysis-result': {
    operation: 'buffer' | 'intersect' | 'union'
    geometry: GeoJSON.Feature | GeoJSON.FeatureCollection
    label?: string
  }
  'clear-analysis-results': Record<string, never>
  /** v2 — tilt the 3D camera. No-op when viewType === '2d'. */
  'tilt-camera': { tilt: number }
  /** v2 — set compass heading. No-op when viewType === '2d'. */
  'set-heading': { heading: number }
  /** v2 — move the 3D camera to an absolute [lon, lat, z] position. */
  'set-camera-position': { position: [number, number, number] }
  /** v2 — auto-frame a feature with sensible tilt/heading. */
  'fly-to-feature': {
    layerId: string
    objectId: number | string
    tilt?: number
    heading?: number
  }
  /** chatbot scene control — switch between 2D and 3D view. */
  'scene:set-view-type': { viewType: '2d' | '3d'; preserveCenter: boolean }
  /** chatbot scene control — move the 3D camera (partial update; omitted fields keep current values). */
  'scene:set-camera': {
    camera: { center?: [number, number]; altitude_m?: number; tilt?: number; heading?: number }
    animate: boolean
  }
  /** chatbot scene control — set 3D scene environment (sun, weather, atmosphere). */
  'scene:set-environment': {
    env: {
      timeOfDay?: 'sunrise' | 'morning' | 'noon' | 'afternoon' | 'sunset' | 'night'
      weather?: 'sunny' | 'cloudy' | 'rainy' | 'snowy' | 'foggy'
      atmosphere?: 'on' | 'off'
      waterReflections?: boolean
      sunPosition?: { azimuth_deg: number; elevation_deg: number }
    }
  }
  /** chatbot scene control — play a saved-map slide by id. */
  'scene:play-slide': { slideId: string }
  /** chatbot scene control — configure per-layer 3D fidelity (elevation, extrusion, edges, labels). */
  'scene:set-layer-3d-fidelity': {
    layerName: string
    fidelity: {
      elevation_mode?: string
      elevation_offset_m?: number
      extrusion_field?: string
      extrusion_unit?: string
      edges_enabled?: boolean
      labels_enabled?: boolean
    }
  }
  /** chatbot floor control — slice a floor-aware building to a level (belowSelected cutaway). */
  'scene:set-active-floor': { buildingId: string; floorValue: number }
  /** chatbot floor control — clear a building's cutaway (omit buildingId to clear all in-view). */
  'scene:clear-active-floor': { buildingId?: string }
  /** chatbot media — request a still capture from a cctv/video tile (keyed by
   *  elementId). requestId = the tool_use id, echoed back on the result. */
  'media:capture-still': { elementId: string; requestId: string }
  /** chatbot media — a tile's capture result (or failure). DashboardChatbot
   *  listens and auto-posts a vision describe turn. */
  'media:capture-still-result': {
    requestId: string
    elementId: string
    title?: string
    dataUrl?: string
    error?: string
  }
  /** chatbot media — switch a cctv tile to playback and seek to a wall-clock time. */
  'cctv:goto-playback': { elementId: string; timestamp: string }
  /** chatbot media — play / pause / return-to-live a cctv or video tile. */
  'media:playback': { elementId: string; command: 'play' | 'pause' | 'live' }
  /** chatbot media — seek a direct <video> tile to an offset in seconds. */
  'video:seek': { elementId: string; seconds: number }
  /** chatbot media — nudge a cctv camera (pan/tilt). */
  'cctv:ptz': { elementId: string; direction: 'up' | 'down' | 'left' | 'right'; amount?: 'small' | 'medium' | 'large' }
  /**
   * robot realtime channel — a live robot position. Payload mirrors the §6
   * `location` frame / the broker snapshot row shape. `seq` is the per-frame
   * monotonic sequence used downstream for snapshot dedup. Emitted by
   * `useLiveChannel`; consumed by the (future) fleet dashboard map.
   */
  'robot:location': {
    robotId: string
    lng: number
    lat: number
    heading?: number | null
    battery?: number | null
    ctrlMode?: number | null
    name?: string | null
    seq?: number
  }
  /** robot realtime channel — a telemetry frame (free-form metrics keyed by the robot). */
  'robot:telemetry': {
    robotId: string
    seq?: number
    metrics?: Record<string, number | string | boolean | null>
  }
  /**
   * robot realtime channel — an alert raised by a robot. Phase 2 widened: the
   * §6 alert payload is `{type, severity?, identity?, message, lat, lng,
   * snapshot?, name?}` plus the server-assigned `alertId` (Mongo _id) +
   * lifecycle `status`. `type` is the alert category (formerly `code`).
   */
  'robot:alert': {
    robotId: string
    seq?: number
    alertId?: string
    type?: string
    severity?: 'info' | 'warning' | 'critical'
    identity?: string
    message?: string
    name?: string
    snapshotRef?: string
    recordedAt?: string
    status?: string
    lng?: number
    lat?: number
  }
  /** robot realtime channel — a connection/lifecycle status change for a robot. */
  'robot:status': {
    robotId: string
    status: 'online' | 'offline' | 'degraded'
    seq?: number
  }
  /**
   * robot fleet dashboard (sub-project 3) — per-dashboard, transient cross-element
   * SELECTION. The fleet map marker click + the roster row click emit this; the
   * map, roster, alerts inbox (followSelection) and cctv pane subscribe.
   * `robotId: null` clears focus back to fleet scope. Lives on the bridge (NOT a
   * module-global store) so selection never leaks across dashboards/tabs.
   */
  'robot:select': { robotId: string | null }
  /**
   * The devices ticked in the device list (`device_list`), in tick order. A SET, unlike
   * `robot:select`'s one focused device: a following helmet wall shows exactly these helmets and
   * pre-ticks them for bulk control. Empty = nothing ticked. Per-dashboard and transient, like
   * `robot:select`; `useRobotMultiSelection` keeps the latest value per bridge so an element that
   * remounts (a tab switch) still knows it.
   */
  'robot:multi-select': { robotIds: string[] }
  /**
   * The operator asked a device for a photo (RobotMediaPanel's Take photo). Nothing is pushed when
   * the picture lands, so a listener that shows pictures — the alert log — uses this to start a
   * short bounded re-check instead of waiting for the next refresh tick.
   */
  'robot:media-requested': { robotId: string }
  /** Alt-click camera-point selection (Alt-Click → CCTV re-scope) AND chatbot
   *  `cctv_select`. cameraId is a CctvCamera._id; null clears the selection back to
   *  each element's config.cameraId. `elementId` (optional) scopes the swap to a
   *  single CCTV tile; omitted = global (every follow-enabled tile, like Alt-click). */
  'cctv:select': { cameraId: string | null; elementId?: string }
  /** controller shortcut — reveal an element (activate its ancestor tabs, un-hide
   *  it, scroll + flash). Emitted by DashboardControllerProvider on a mapped
   *  button press; consumed by DashboardView's canvas. */
  'controller:reveal': { elementId: string }
  /**
   * chatbot robot control (Mode 3) — say these words out loud, VERBATIM.
   *
   * The payload has already passed the AI tool's guards (robot resolved, online,
   * voice advertised, within the robot's own `maxChars`, language dropped unless
   * advertised) and is re-checked by `normaliseSpeakText` at the element before
   * it goes on the wire. Two checks on purpose: the tool's is what lets the
   * model correct itself, the element's is what makes it impossible for ANY
   * caller — panel, preset, macro or model — to reach the robot's voice with
   * text the console never inspected.
   *
   * The element sends it through the SAME `useRobotCommandSender` a button press
   * uses, so the broker's operator-role check and rate limit still apply. The
   * model never emits a robot command; it emits an intent, and the browser
   * decides whether that becomes one.
   */
  'robot:speak': { robotId: string; text: string; language?: string }
  /** chatbot robot control — play ONE advertised motion or expression. `name` is
   *  the exact advertised id (gesture name / linkcraft key / expression id as a
   *  string); `kind` says which catalogue it came from. Whole-body (`big`)
   *  motions never reach here — the tool refuses them upstream with a reason. */
  'robot:perform': { robotId: string; kind: 'gesture' | 'linkcraft' | 'face'; name: string }
  /** chatbot robot control — cut the current utterance short. This is NOT an
   *  E-stop and must never be wired to one: see `tools/robot/robotStop.ts`. */
  'robot:stop-speaking': { robotId: string }
}

export type BridgeRequests = {
  'map-state': MapStateSnapshot
  'layer-samples': LayerSampleSnapshot[]
  /** On-map CCTV cameras (catalog ∩ map cctv_id points), cached by DashboardMap. */
  'cctv-cameras': CctvCameraOnMap[]
  /** Floor-aware buildings visible on the map + their active floor. Answered by DashboardMap. */
  'floor-context': FloorContext[]
  /**
   * Crawl results for every mounted `arcgis_dashboard` element on this
   * dashboard — the chatbot's window into embedded Esri Dashboards' widgets/
   * layers/dataSources. There can be N `arcgis_dashboard` elements on one
   * dashboard; `onRequest` supports multiple handlers per kind (Task 12), but
   * a `request()` still resolves from just the first one that answers, and
   * this kind needs the FULL list. So `DashboardBridgeProvider` still answers
   * it from a per-element registry (`registerArcgisDashboardContext`/
   * `unregisterArcgisDashboardContext`) with ONE handler aggregating every
   * element's crawl, rather than each element registering its own handler
   * and only the first-resolving one being seen. Collapsing this registry
   * onto a plain multi-handler request is Phase 2a ActionRegistry scope.
   */
  'arcgis-dashboard-context': ArcgisDashboardCrawl[]
  /**
   * Every robot currently mounted on this dashboard, with what each one says it
   * can do. Answered from the per-dashboard robot registry in
   * `DashboardBridgeProvider` for the SAME reason `arcgis-dashboard-context` is:
   * a dashboard can host several robot elements (a teleop tile and a feed tile
   * for the same fleet, say), but `onRequest` keeps only ONE handler per kind,
   * so per-element registration would have each robot silently clobber its
   * siblings and the chatbot would see exactly one of them — whichever mounted
   * last.
   */
  robots: RobotChatContext[]
}

export interface DashboardBridge {
  emit<E extends keyof BridgeEvents>(event: E, payload: BridgeEvents[E]): void
  on<E extends keyof BridgeEvents>(event: E, listener: (payload: BridgeEvents[E]) => void): () => void
  request<K extends keyof BridgeRequests>(kind: K, opts?: { timeoutMs?: number }): Promise<BridgeRequests[K]>
  onRequest<K extends keyof BridgeRequests>(
    kind: K,
    handler: () => BridgeRequests[K] | Promise<BridgeRequests[K]>,
  ): () => void
}

export function createDashboardBridge(): DashboardBridge {
  const listeners = new Map<string, Set<(payload: unknown) => void>>()
  // A Set per kind, not a single handler: two maps on one dashboard both
  // register `map-state`, and the old `.set` silently clobbered one of them.
  const requestHandlers = new Map<string, Set<() => unknown>>()

  return {
    emit(event, payload) {
      const set = listeners.get(event as string)

      if (!set) return

      // Snapshot: a listener may unsubscribe itself (or subscribe another)
      // during dispatch, which would otherwise mutate the Set mid-iteration.
      for (const l of [...set]) {
        try {
          l(payload as unknown)
        } catch (err) {
          // Contain it. Previously one throwing subscriber aborted every later
          // subscriber AND propagated into the emitter -- which, from
          // dispatchToolToBridge, abandoned the rest of the turn's tool calls.
          console.error(`DashboardBridge: listener for "${String(event)}" threw`, err)
        }
      }
    },
    on(event, listener) {
      const key = event as string

      if (!listeners.has(key)) listeners.set(key, new Set())

      const set = listeners.get(key)!

      set.add(listener as (payload: unknown) => void)

      // Mirrors `onRequest`'s cleanup below: drop the now-empty Set from the
      // map rather than leaving it behind. Previously `on` left an empty Set
      // for every event kind that had ever been subscribed to, for the life
      // of the bridge.
      return () => {
        set.delete(listener as (payload: unknown) => void)
        if (set.size === 0) listeners.delete(key)
      }
    },
    async request(kind, opts) {
      const timeoutMs = opts?.timeoutMs ?? 2000
      const set = requestHandlers.get(kind as string)

      let timer: ReturnType<typeof setTimeout> | undefined
      // The timeout is built BEFORE the handler runs. Previously the
      // `if (handler) return await handler()` early-return happened first, so a
      // registered-but-hanging handler hung forever -- and DashboardChatbot
      // awaits five of these sequentially.
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`DashboardBridge.request("${String(kind)}") timeout`)), timeoutMs)
      })

      try {
        if (!set || set.size === 0) return await timeout

        // Deterministic: insertion order, first handler that resolves wins. A
        // handler that throws is skipped rather than failing the whole request.
        const run = (async () => {
          let lastErr: unknown

          for (const handler of [...set]) {
            try {
              return (await handler()) as never
            } catch (err) {
              lastErr = err
            }
          }

          throw lastErr ?? new Error(`DashboardBridge.request("${String(kind)}") had no usable handler`)
        })()

        return await Promise.race([run, timeout])
      } finally {
        // The old code leaked this timer on every no-handler call.
        if (timer) clearTimeout(timer)
      }
    },
    onRequest(kind, handler) {
      const key = kind as string

      if (!requestHandlers.has(key)) requestHandlers.set(key, new Set())

      const set = requestHandlers.get(key)!

      set.add(handler as () => unknown)

      // Closes over the exact handler reference. The old unconditional
      // `.delete(kind)` removed whoever was registered, so a torn-down
      // element's stale cleanup could kill a live element's handler --
      // and DashboardMap re-runs its registration effect on any of 21 deps.
      return () => {
        set.delete(handler as () => unknown)
        if (set.size === 0) requestHandlers.delete(key)
      }
    },
  }
}
